/**
 * The quote page's buttons and bands — folder 52, Board 2
 * (`docs/job-stages.md` §1, §4, §5, §6).
 *
 * Nothing here decides a button: `jobActions(…, quoteId)` (served by `GET
 * /api/v1/jobs/:id/actions?quote=`) says which actions the quote has and in
 * which slot — the **secondary** white, the **primary** green right-most
 * beside ⋯, the rest in ⋯ in the order drawn. The words are
 * `job-stages/copy.ts`'s, the glyphs and the ⋯ rows the job page's
 * (`pages/job-stage-header.tsx`), so the two pages cannot drift.
 *
 * Under the bar, a **grey band** on a closed quote and on every quote of a
 * cancelled job, and the **amber band** on a draft while another version is
 * with the customer (§6) — `BAND`'s sentences from the tree's `closed` facts.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { JobActionItem, JobActionsAnswer, QuoteTree } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { formatDateOnly, isDayOnly } from '../lib/format.js'
import { ContextMenu, type MenuItem } from '../components/context-menu.js'
import { StageGlyph } from '../job-stages/action-glyphs.js'
import {
  BAND, QUOTE_TAG, TOAST, actionLabel, quoteVersion, type BandWords,
} from '../job-stages/copy.js'
import { BUTTON_GLYPH, stageMenu } from '../pages/job-stage-header.js'
import type { QuoteBuilder } from '../builder/adapters/quote.js'

// ---------------------------------------------------------------------------
// The actions
// ---------------------------------------------------------------------------

/** The five actions Board 3 draws a popup for. */
export type StagePopupKey = 'approve' | 'mark_quoted' | 'withdraw_quote' | 'mark_declined' | 'cancel_job'

export interface QuoteStage {
  /** `jobActions()` for this quote, with the job and its quotes; null until read. */
  actions: JobActionsAnswer | null
  /** Do what an action says (§5). */
  run: (item: JobActionItem) => void
  /** The Board 3 popup that is open, and the item it was opened for. */
  popup: { key: StagePopupKey; item: JobActionItem } | null
  closePopup: () => void
  /** A popup's write went through: read everything again and say so in a white toast. */
  done: (toast: string | null) => void
  /** Read the buttons again (after Send, which changes the job too). */
  reload: () => void
}

/**
 * The quote's buttons, read when the quote or its job changes state — not on
 * every line edit, which bumps the tree's version and changes no button.
 */
export function useQuoteStage(qb: QuoteBuilder): QuoteStage {
  const { quote, navigate, openTab, mutate, load, setSentNote, setError } = qb
  const [actions, setActions] = useState<JobActionsAnswer | null>(null)
  const [popup, setPopup] = useState<QuoteStage['popup']>(null)
  const jobId = quote?.jobId
  const quoteId = quote?.id
  const live = useRef(0)

  const reload = useCallback(() => {
    if (!jobId || !quoteId) return
    const ask = ++live.current
    void request<JobActionsAnswer>(`/jobs/${encodeURIComponent(jobId)}/actions?quote=${encodeURIComponent(quoteId)}`)
      .then((answer) => { if (ask === live.current) setActions(answer) })
      .catch(() => { if (ask === live.current) setActions(null) })
  }, [jobId, quoteId])

  useEffect(() => { reload() }, [reload, quote?.status, quote?.closed?.job.status])

  const toast = useCallback((text: string) => setSentNote({ tone: 'default', text }), [setSentNote])

  const done = useCallback((text: string | null) => {
    setPopup(null)
    void load()
    reload()
    if (text) toast(text)
  }, [load, reload, toast])

  const run = useCallback((item: JobActionItem) => {
    if (!quote) return
    switch (item.key) {
      case 'preview':
        openTab('proposal')
        return
      case 'send':
        /* Today's send (§5): its checks, its email, its toast — now white. */
        void mutate('/send', { method: 'POST' }).then((ok) => { if (ok) reload() })
        return
      case 'approve':
      case 'mark_quoted':
      case 'withdraw_quote':
      case 'mark_declined':
      case 'cancel_job':
        setPopup({ key: item.key, item })
        return
      case 'new_quote':
        /* From a quote page: the next version as a copy of it (`copyFrom`), opened. */
        void request<{ id: string }>('/quotes', {
          method: 'POST',
          body: { jobId: quote.jobId, ...(item.copy && item.quote ? { copyFrom: item.quote.id } : {}) },
        })
          .then((made) => navigate(`/quotes/${made.id}`))
          .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'That did not save.'))
        return
      case 'open_quote':
      case 'open_draft':
      case 'open_version':
      case 'open_agreement':
        if (item.quote) navigate(`/quotes/${item.quote.id}`)
        return
      case 'reopen_request':
        /* No popup (§5): the job is a request again, and a white toast says so. */
        void request(`/jobs/${encodeURIComponent(quote.jobId)}/reopen`, { method: 'POST' })
          .then(() => done(TOAST.requestAgain(quote.jobReference)))
          .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'That did not save.'))
        return
      case 'schedule':
        /* The Schedule focused on this job (§5). */
        navigate(`/schedule?job=${encodeURIComponent(quote.jobId)}`)
        return
      case 'check_crew':
        /* Today's button, moved into ⋯. */
        navigate(`/schedule?quote=${encodeURIComponent(quote.id)}`)
        return
      default:
        /* The job page's own (Edit, Start work, Complete) — never offered on a quote (§4). */
        return
    }
  }, [quote, openTab, mutate, reload, navigate, setError, done])

  return { actions, run, popup, closePopup: () => setPopup(null), done, reload }
}

// ---------------------------------------------------------------------------
// The bar
// ---------------------------------------------------------------------------

/**
 * Board 2's button: 36 tall, a 10 px radius, 14 px sides, 12.5 semibold, the
 * 14.5 glyph 6 px from the words — the job header's, at the same size.
 */
const STAGE_BUTTON = 'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap'
  + ' rounded-button-toolbar border px-3.5 text-[12.5px] font-semibold leading-[normal]'

/** One of the bar's two buttons. */
function BarButton({ item, tone, actions, onRun, disabled, title }: {
  item: JobActionItem
  tone: 'secondary' | 'primary'
  actions: JobActionsAnswer
  onRun: (item: JobActionItem) => void
  disabled?: boolean
  title?: string
}) {
  const glyph = BUTTON_GLYPH[item.key]
  return (
    <button
      type="button"
      data-button={tone}
      data-action={item.key}
      disabled={disabled}
      title={title}
      onClick={() => onRun(item)}
      className={cn(STAGE_BUTTON, 'disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'primary'
          ? 'border-accent bg-accent text-white enabled:hover:bg-accent-dark'
          : 'border-line bg-surface text-text enabled:hover:bg-hover')}
    >
      {glyph && <StageGlyph name={glyph} size={14.5} />}
      {actionLabel(item, actions.firstName)}
    </button>
  )
}

/**
 * The ⋯: white and 36 square closed, tinted (accent-soft, accent-line,
 * accent-dark) while its menu is open, as Boards 1 and 2 draw it open. The
 * menu drops from its right edge.
 */
export function StageMore({ items, phone = false }: { items: MenuItem[]; phone?: boolean }) {
  const [open, setOpen] = useState<{ x: number; y: number } | null>(null)
  const at = useRef<HTMLButtonElement>(null)
  if (items.length === 0) return null
  const toggle = () => {
    if (open) { setOpen(null); return }
    const box = at.current?.getBoundingClientRect()
    /* The 330 menu under the button, its right edge on the button's. */
    setOpen(box ? { x: Math.max(8, box.right - 330), y: box.bottom + 6 } : null)
  }
  return (
    <>
      <button
        ref={at}
        type="button"
        aria-label="More actions"
        aria-expanded={open !== null}
        data-stage-more=""
        onClick={toggle}
        className={cn('inline-flex shrink-0 items-center justify-center',
          phone
            ? cn('h-9 w-9 rounded-field', open ? 'bg-accent-soft text-accent-dark' : 'text-text-muted')
            : cn('h-9 w-9 rounded-button-toolbar border',
              open ? 'border-accent-line bg-accent-soft text-accent-dark'
                : 'border-line bg-surface text-text hover:bg-hover'))}
      >
        <StageGlyph name="more" size={phone ? 18 : 16} stroke={2.4} />
      </button>
      {open && <ContextMenu items={items} x={open.x} y={open.y} onClose={() => setOpen(null)} />}
    </>
  )
}

/**
 * The desktop and iPad bar (§4): the secondary white, the primary green
 * right-most, then ⋯ — nothing at all until the buttons are read, rather
 * than today's buttons for a moment.
 */
export function StageBar({ stage, blocking, busy }: {
  stage: QuoteStage
  /** Send keeps today's guard: a blocking *Before you send* row holds it. */
  blocking: { label: string }[]
  busy: boolean
}) {
  const { actions, run } = stage
  if (!actions || actions.asToday) return null
  const sendHeld = (item: JobActionItem) => item.key === 'send' && (blocking.length > 0 || busy)
  return (
    <div className="flex items-center gap-2" data-stage-bar="">
      {actions.secondary && <BarButton item={actions.secondary} tone="secondary" actions={actions} onRun={run} />}
      {actions.primary && (
        <BarButton
          item={actions.primary}
          tone="primary"
          actions={actions}
          onRun={run}
          disabled={sendHeld(actions.primary)}
          title={actions.primary.key === 'send' ? blocking[0]?.label : undefined}
        />
      )}
      <StageMore items={stageMenu(actions, run, { phone: false, page: 'quote' })} />
    </div>
  )
}

/**
 * The phone's ⋯, right of the eye (§1): the secondary and the rest — the
 * primary is the sticky bar's green button.
 */
export function phoneStageItems(stage: QuoteStage): MenuItem[] {
  const { actions, run } = stage
  if (!actions || actions.asToday) return []
  return stageMenu({ ...actions, primary: null }, run, { phone: true, page: 'quote' })
}

/** The sticky totals bar's green button (§1): `jobActions()`'s primary, or nothing. */
export function stagePrimary(stage: QuoteStage, blocking: number, busy: boolean): {
  label: string; icon: ReactNode; onClick: () => void; disabled: boolean
} | null {
  const { actions, run } = stage
  const item = actions && !actions.asToday ? actions.primary : null
  if (!actions || !item) return null
  const glyph = BUTTON_GLYPH[item.key]
  return {
    label: actionLabel(item, actions.firstName),
    icon: glyph ? <StageGlyph name={glyph} size={16} /> : null,
    onClick: () => run(item),
    disabled: item.key === 'send' && (blocking > 0 || busy),
  }
}

// ---------------------------------------------------------------------------
// The chip
// ---------------------------------------------------------------------------

/**
 * Board 2's tag beside the title: *Draft v1* amber, *Viewed* blue, *Approved*
 * green, *Expired · v1* · *Replaced · v3* · *Declined* grey — 20 tall, 6
 * round, 11 semibold. A sent quote not yet viewed keeps today's *Sent*, in
 * the viewed blue (the board draws the pair as one row).
 */
export function QuoteChip({ quote }: { quote: QuoteTree }) {
  const { status, quoteVersion: version } = quote
  const words = status === 'draft' ? QUOTE_TAG.draft(version)
    : status === 'viewed' ? QUOTE_TAG.viewed
      : status === 'approved' ? QUOTE_TAG.approved
        : status === 'declined' ? QUOTE_TAG.declined
          : status === 'expired' ? QUOTE_TAG.expired(version)
            : status === 'replaced' ? QUOTE_TAG.replaced(version)
              : status[0]!.toUpperCase() + status.slice(1)
  return (
    <span
      data-quote-chip={status}
      className={cn('inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-[6px] px-[7px]',
        'text-[11px] font-semibold leading-[normal]',
        status === 'draft' ? 'bg-[rgb(var(--status-roleTone-amber-bg))] text-[rgb(var(--status-roleTone-amber-fg))]'
          : status === 'sent' || status === 'viewed' ? 'bg-info-bg text-info'
            : status === 'approved' ? 'bg-accent-soft text-accent-dark'
              : 'bg-line-soft text-text-muted')}
    >
      {words}
    </span>
  )
}

// ---------------------------------------------------------------------------
// The bands (§6)
// ---------------------------------------------------------------------------

const monthDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

/** *Oct 6* — a day as a day, an instant where the reader is. */
export function shortDay(value: string | null | undefined): string {
  if (!value) return ''
  if (isDayOnly(value)) return formatDateOnly(value)
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : monthDay.format(date)
}

type BandKind = 'grey' | 'amber'
type BandGlyph = 'lock' | 'close' | 'info'

/** What the band under this quote's bar says, if anything. */
export function quoteBand(quote: QuoteTree, actions: JobActionsAnswer | null): {
  kind: BandKind; glyph: BandGlyph; words: BandWords
} | null {
  const closed = quote.closed
  if (!closed) return null
  const firstName = actions?.firstName ?? null

  /* Every quote while the job is cancelled (§6, Board 2 row 9). */
  if (closed.job.status === 'cancelled') {
    return {
      kind: 'grey',
      glyph: 'lock',
      words: BAND.jobCancelled(shortDay(closed.job.at), closed.job.why ?? '', !closed.job.everApproved),
    }
  }

  switch (quote.status) {
    case 'draft': {
      /* Amber: another version is with the customer (§4 row 2). */
      const out = actions?.withCustomerBand
      if (!out?.sentOn) return null
      return {
        kind: 'amber',
        glyph: 'info',
        words: BAND.withCustomer(quoteVersion(out.reference, out.version), firstName,
          formatDateOnly(out.sentOn), out.viewedOn ? formatDateOnly(out.viewedOn) : null),
      }
    }
    case 'expired': {
      const on = shortDay(closed.at ?? quote.expiresOn)
      if (closed.reason === 'superseded' && closed.replacedBy) {
        return { kind: 'grey', glyph: 'lock', words: BAND.superseded(on, closed.replacedBy.version) }
      }
      if (closed.reason === 'withdrawn') {
        return { kind: 'grey', glyph: 'lock', words: BAND.withdrawn(on, closed.byName ?? '', closed.why ?? '') }
      }
      if (closed.reason === 'job_cancelled') {
        return { kind: 'grey', glyph: 'lock', words: BAND.jobCancelledExpired(on) }
      }
      /* By date — and an older row with no reason, which §9.10 calls by date. */
      return { kind: 'grey', glyph: 'lock', words: BAND.byDate(on, closed.validDays ?? 0) }
    }
    case 'replaced':
      return closed.replacedBy
        ? {
          kind: 'grey',
          glyph: 'lock',
          words: BAND.replaced(shortDay(closed.at), closed.approvedByFirstName ?? firstName, closed.replacedBy.version),
        }
        : null
    case 'declined':
      return {
        kind: 'grey',
        glyph: 'close',
        words: BAND.declined(closed.declinedByFirstName ?? firstName, shortDay(closed.declinedAt), closed.why ?? ''),
      }
    default:
      return null
  }
}

/** The band's own two glyphs (Board 2): the lock and the info circle, at 15 and 2.2. */
function BandGlyphSvg({ name }: { name: BandGlyph }) {
  if (name === 'close') return <StageGlyph name="close" size={15} stroke={2.2} />
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} aria-hidden="true" className="shrink-0"
      style={{ stroke: 'currentColor', fill: 'none', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
      {name === 'lock'
        ? <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>
        : <><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7.5v.01" /></>}
    </svg>
  )
}

/**
 * Board 2's band: 12 round, 11/14 padding, 13 at 1.45, a 30 square tile with
 * the glyph; the lead bold. Grey on `surface-alt` with the line; amber on the
 * amber tokens.
 */
export function QuoteBand({ quote, actions, phone }: {
  quote: QuoteTree; actions: JobActionsAnswer | null; phone: boolean
}) {
  const band = quoteBand(quote, actions)
  if (!band) return null
  const amber = band.kind === 'amber'
  return (
    <div className={cn('shrink-0', phone ? 'px-4 pt-3' : 'px-6 pt-3.5')}>
      <div
        role="note"
        data-quote-band={band.kind}
        className={cn('flex items-center gap-3 rounded-touch border px-3.5 py-[11px] text-[13px] leading-[1.45]',
          amber
            ? 'border-amber-band-line bg-amber-soft text-amber-band-text'
            : 'border-line bg-surface-alt text-text-body')}
      >
        <span
          className={cn('inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-tile border bg-surface',
            amber ? 'border-amber-band-line text-[rgb(var(--status-roleTone-amber-fg))]' : 'border-line text-text-muted')}
        >
          <BandGlyphSvg name={band.glyph} />
        </span>
        <span>
          <b className="font-bold">{band.words.lead}</b>
          {band.words.rest ? ` ${band.words.rest}` : null}
        </span>
      </div>
    </div>
  )
}
